import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Music, Sparkles, Brain, Zap, Radio, Loader2 } from 'lucide-react';
import type { MoodContext } from '@/types';
import { getToken } from '@/lib/auth';

interface Artist {
  id: number;
  spotify_id: string;
  name: string;
  genres: string[];
  image_url?: string;
  popularity?: number;
}

interface GenerationEngine {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  minArtists: number;
  maxArtists: number;
}

interface PlaylistGeneratorPanelProps {
  onGenerationComplete: (playlistId: number) => void;
}

const ENGINES: GenerationEngine[] = [
  {
    id: 'lastfm',
    name: 'Last.fm Collaborative',
    description: 'Uses 3-artist combinations with Last.fm listening data for discovery',
    icon: <Sparkles size={20} />,
    minArtists: 10,
    maxArtists: 100,
  },
  {
    id: 'hybrid',
    name: 'Hybrid AI',
    description: 'Combines multiple AI algorithms for personalized recommendations',
    icon: <Brain size={20} />,
    minArtists: 5,
    maxArtists: 50,
  },
  {
    id: 'mood',
    name: 'Mood-Based',
    description: 'Filter by audio features and mood categories',
    icon: <Zap size={20} />,
    minArtists: 1,
    maxArtists: 30,
  },
  {
    id: 'auralist',
    name: 'Auralist LDA',
    description: 'Topic modeling with serendipity scoring',
    icon: <Music size={20} />,
    minArtists: 5,
    maxArtists: 50,
  },
  {
    id: 'artist_radio',
    name: 'Artist Radio',
    description: 'Deep dive into similar artists',
    icon: <Radio size={20} />,
    minArtists: 1,
    maxArtists: 10,
  },
];

const MOODS: { value: MoodContext; label: string; emoji: string }[] = [
  { value: 'energetic', label: 'Energetic', emoji: '⚡' },
  { value: 'chill', label: 'Chill', emoji: '😌' },
  { value: 'happy', label: 'Happy', emoji: '😊' },
  { value: 'party', label: 'Party', emoji: '🎉' },
  { value: 'focus', label: 'Focus', emoji: '🎯' },
  { value: 'workout', label: 'Workout', emoji: '💪' },
  { value: 'sad', label: 'Sad', emoji: '😢' },
  { value: 'relax', label: 'Relax', emoji: '🧘' },
];

/**
 * PlaylistGeneratorPanel Component
 *
 * Main UI for generating playlists with multiple engines.
 * Features:
 * - Engine selection with descriptions
 * - Artist selection from imported playlists
 * - Mood filtering
 * - Configurable playlist size (50-500 tracks)
 * - Real-time generation progress
 */
export const PlaylistGeneratorPanel: React.FC<PlaylistGeneratorPanelProps> = ({
  onGenerationComplete,
}) => {
  const [step, setStep] = useState<'engine' | 'config' | 'generating'>('engine');
  const [selectedEngine, setSelectedEngine] = useState<string>('lastfm');
  const [playlistName, setPlaylistName] = useState('');
  const [playlistDescription, setPlaylistDescription] = useState('');
  const [selectedArtistIds, setSelectedArtistIds] = useState<number[]>([]);
  const [selectedMood, setSelectedMood] = useState<MoodContext | null>(null);
  const [playlistSize, setPlaylistSize] = useState(250);
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const engine = ENGINES.find(e => e.id === selectedEngine);

  // Load all artists on mount
  useEffect(() => {
    fetchAllArtists();
  }, []);

  // Filter artists when mood changes
  useEffect(() => {
    if (selectedMood) {
      fetchArtistsByMood(selectedMood);
    } else {
      setFilteredArtists(allArtists);
    }
  }, [selectedMood, allArtists]);

  const fetchAllArtists = async () => {
    setLoadingArtists(true);
    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/artists?limit=200', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAllArtists(data.artists || []);
        setFilteredArtists(data.artists || []);
      }
    } catch (err) {
      console.error('Failed to fetch artists:', err);
    } finally {
      setLoadingArtists(false);
    }
  };

  const fetchArtistsByMood = async (mood: MoodContext) => {
    setLoadingArtists(true);
    try {
      const token = getToken();
      const response = await fetch(`/automusic/api/playlists/artists/by-mood/${mood}?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFilteredArtists(data);
      }
    } catch (err) {
      console.error('Failed to fetch artists by mood:', err);
    } finally {
      setLoadingArtists(false);
    }
  };

  const handleEngineSelect = (engineId: string) => {
    setSelectedEngine(engineId);
    setStep('config');
  };

  const handleArtistToggle = (artistId: number) => {
    setSelectedArtistIds(prev =>
      prev.includes(artistId)
        ? prev.filter(id => id !== artistId)
        : [...prev, artistId]
    );
  };

  const handleGenerate = async () => {
    if (!playlistName || selectedArtistIds.length === 0) return;

    setGenerating(true);
    setError(null);
    setStep('generating');

    try {
      const token = getToken();
      const response = await fetch('/automusic/api/playlists/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: playlistName,
          description: playlistDescription,
          engine: selectedEngine,
          artist_ids: selectedArtistIds,
          size: playlistSize,
          mood: selectedMood,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate playlist');
      }

      const result = await response.json();
      onGenerationComplete(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate playlist');
      setStep('config');
    } finally {
      setGenerating(false);
    }
  };

  const canGenerate = () => {
    if (!engine) return false;
    return (
      playlistName.trim() !== '' &&
      selectedArtistIds.length >= engine.minArtists &&
      selectedArtistIds.length <= engine.maxArtists
    );
  };

  return (
    <div className="bg-gray-900 rounded-lg shadow-xl p-6">
      {/* Step 1: Engine Selection */}
      {step === 'engine' && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Choose Generation Engine</h2>
          <p className="text-gray-400 mb-6">Select how you want to discover music</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ENGINES.map((eng) => (
              <button
                key={eng.id}
                onClick={() => handleEngineSelect(eng.id)}
                className={clsx(
                  'p-4 rounded-lg border-2 transition-all text-left',
                  'hover:border-spotify-green hover:bg-gray-800',
                  {
                    'border-spotify-green bg-gray-800': selectedEngine === eng.id,
                    'border-gray-700 bg-gray-850': selectedEngine !== eng.id,
                  }
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="text-spotify-green mt-1">{eng.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{eng.name}</h3>
                    <p className="text-sm text-gray-400 mb-2">{eng.description}</p>
                    <p className="text-xs text-gray-500">
                      {eng.minArtists}-{eng.maxArtists} artists recommended
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Configuration */}
      {step === 'config' && engine && (
        <div>
          <button
            onClick={() => setStep('engine')}
            className="text-spotify-green hover:underline mb-4 text-sm"
          >
            ← Change Engine
          </button>

          <h2 className="text-2xl font-bold text-white mb-2">Configure Playlist</h2>
          <p className="text-gray-400 mb-6">
            Using: <span className="text-spotify-green">{engine.name}</span>
          </p>

          {/* Playlist Name */}
          <div className="mb-4">
            <label className="block text-white font-semibold mb-2">Playlist Name *</label>
            <input
              type="text"
              value={playlistName}
              onChange={(e) => setPlaylistName(e.target.value)}
              placeholder="My Awesome Playlist"
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-spotify-green"
            />
          </div>

          {/* Playlist Description */}
          <div className="mb-4">
            <label className="block text-white font-semibold mb-2">Description (Optional)</label>
            <textarea
              value={playlistDescription}
              onChange={(e) => setPlaylistDescription(e.target.value)}
              placeholder="Describe your playlist..."
              rows={2}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-spotify-green"
            />
          </div>

          {/* Playlist Size */}
          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">
              Playlist Size: {playlistSize} tracks
            </label>
            <input
              type="range"
              min="50"
              max="500"
              step="50"
              value={playlistSize}
              onChange={(e) => setPlaylistSize(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>50</span>
              <span>250</span>
              <span>500</span>
            </div>
          </div>

          {/* Artist Selection */}
          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">
              Select Artists ({selectedArtistIds.length}/{engine.minArtists}-{engine.maxArtists})
            </label>

            {loadingArtists ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-spotify-green" size={32} />
              </div>
            ) : filteredArtists.length === 0 ? (
              <div className="text-center py-8 bg-gray-800 rounded-lg">
                <Music className="mx-auto mb-2 text-gray-500" size={32} />
                <p className="text-gray-400">
                  No artists found. Try importing playlists first.
                </p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto bg-gray-800 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {filteredArtists.slice(0, 100).map((artist) => (
                    <button
                      key={artist.id}
                      onClick={() => handleArtistToggle(artist.id)}
                      className={clsx(
                        'p-2 rounded-lg text-sm transition text-left',
                        {
                          'bg-spotify-green text-black': selectedArtistIds.includes(artist.id),
                          'bg-gray-700 text-white hover:bg-gray-600': !selectedArtistIds.includes(artist.id),
                        }
                      )}
                    >
                      {artist.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedArtistIds.length < engine.minArtists && (
              <p className="text-yellow-500 text-sm mt-2">
                Select at least {engine.minArtists} artists
              </p>
            )}
          </div>

          {/* Mood Filter */}
          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">
              Filter by Mood (Optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {MOODS.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => setSelectedMood(mood.value === selectedMood ? null : mood.value)}
                  className={clsx(
                    'px-3 py-2 rounded-full text-sm font-medium transition',
                    {
                      'bg-spotify-green text-black': selectedMood === mood.value,
                      'bg-gray-800 text-gray-300 hover:bg-gray-700': selectedMood !== mood.value,
                    }
                  )}
                >
                  <span className="mr-1">{mood.emoji}</span>
                  {mood.label}
                </button>
              ))}
            </div>
            {selectedMood && (
              <p className="text-xs text-gray-400 mt-2">
                Showing {filteredArtists.length} artists matching "{selectedMood}" mood
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!canGenerate() || generating}
            className={clsx(
              'w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2',
              {
                'bg-spotify-green text-black hover:bg-green-400': canGenerate() && !generating,
                'bg-gray-700 text-gray-500 cursor-not-allowed': !canGenerate() || generating,
              }
            )}
          >
            {generating ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Generating...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Generate Playlist
              </>
            )}
          </button>
        </div>
      )}

      {/* Step 3: Generating */}
      {step === 'generating' && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="animate-spin text-spotify-green mb-4" size={64} />
          <h3 className="text-xl font-bold text-white mb-2">Generating Your Playlist</h3>
          <p className="text-gray-400 text-center mb-4">
            Using {engine?.name} to discover {playlistSize} amazing tracks...
          </p>
          <p className="text-gray-500 text-sm">This may take a minute</p>
        </div>
      )}
    </div>
  );
};

export default PlaylistGeneratorPanel;
